import { zeroErrorMessage } from "@/lib/zero-error";
import { useQuery as useZeroQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { toFriendlyMutationError } from "@/lib/error-utils";
import { generateUUID } from "@/lib/utils";
import {
  getEquipmentTagDescendantIds,
  useEquipmentTags,
} from "@/hooks/useEquipmentTags";

export type EquipmentImage = {
  id: string;
  equipment_id: string;
  image_url: string;
  display_order: number | null;
  created_at: string | null;
};

export type EquipmentTagSummary = {
  id: string;
  name: string;
};

export type Equipment = {
  id: string;
  number: string | null;
  name: string;
  manufacturer: string | null;
  icon: string | null;
  new_price: number | null;
  rental_price: number | null;
  is_serialized: boolean;
  stock_quantity: number | null;
  notes: string | null;
  parent_equipment_id: string | null;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
  version: number | null;
  rentman_id: number | null;
  weight: number | null;
  width: number | null;
  height: number | null;
  length: number | null;
  volume: number | null;
  power: number | null;
  current_draw: number | null;
  qr_code: string | null;
  external_remark: string | null;
  location_in_warehouse: string | null;
  short_name: string | null;
  country_of_origin: string | null;
  customs_tariff_number: string | null;
  carnet_ata_description: string | null;
  carnet_ata_value: number | null;
  carnet_ata_weight_unit: string | null;
  tags?: EquipmentTagSummary[];
  images?: EquipmentImage[];
  instances_count?: number;
};

export type EquipmentInsert = Partial<Omit<Equipment, "id">> & {
  name: string;
  is_serialized: boolean;
};
export type EquipmentUpdate = Partial<EquipmentInsert>;

export const PAGE_SIZE = 100;

interface UseEquipmentOptions {
  search?: string;
  tagIds?: string[];
  isSerialized?: boolean | null;
  page?: number;
}

export interface PaginatedEquipment {
  items: Equipment[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

type ZeroMutationResult =
  | { type: "error"; error: { message: string } }
  | { type: string; error?: never };
type ZeroMutateClient = {
  mutate: (mutation: unknown) => {
    client: Promise<void>;
    server: Promise<ZeroMutationResult>;
  };
};
type MutationOptions<T> = { onSuccess?: (data: T) => void };

type ZeroEquipmentRow = {
  id: string;
  number?: string | null;
  name: string;
  manufacturer?: string | null;
  icon?: string | null;
  newPrice?: number | null;
  rentalPrice?: number | null;
  isSerialized: boolean;
  stockQuantity?: number | null;
  notes?: string | null;
  parentEquipmentID?: string | null;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
  deletedAt?: number | string | null;
  version?: number | null;
  rentmanID?: number | null;
  weight?: number | null;
  width?: number | null;
  height?: number | null;
  length?: number | null;
  volume?: number | null;
  power?: number | null;
  currentDraw?: number | null;
  qrCode?: string | null;
  externalRemark?: string | null;
  locationInWarehouse?: string | null;
  shortName?: string | null;
  countryOfOrigin?: string | null;
  customsTariffNumber?: string | null;
  carnetAtaDescription?: string | null;
  carnetAtaValue?: number | null;
  carnetAtaWeightUnit?: string | null;
  tagAssignments?: {
    tag?: { id: string; name: string } | null;
  }[];
  images?: {
    id: string;
    equipmentID: string;
    imageURL: string;
    displayOrder?: number | null;
    createdAt?: number | string | null;
  }[];
};

function toLegacyTimestamp(value: number | string | null | undefined) {
  if (typeof value === "number") return new Date(value).toISOString();
  return value ?? null;
}

function toLegacyEquipment(row: ZeroEquipmentRow): Equipment {
  return {
    id: row.id,
    number: row.number ?? null,
    name: row.name,
    manufacturer: row.manufacturer ?? null,
    icon: row.icon ?? null,
    new_price: row.newPrice ?? null,
    rental_price: row.rentalPrice ?? null,
    is_serialized: row.isSerialized,
    stock_quantity: row.stockQuantity ?? null,
    notes: row.notes ?? null,
    parent_equipment_id: row.parentEquipmentID ?? null,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
    deleted_at: toLegacyTimestamp(row.deletedAt),
    version: row.version ?? null,
    rentman_id: row.rentmanID ?? null,
    weight: row.weight ?? null,
    width: row.width ?? null,
    height: row.height ?? null,
    length: row.length ?? null,
    volume: row.volume ?? null,
    power: row.power ?? null,
    current_draw: row.currentDraw ?? null,
    qr_code: row.qrCode ?? null,
    external_remark: row.externalRemark ?? null,
    location_in_warehouse: row.locationInWarehouse ?? null,
    short_name: row.shortName ?? null,
    country_of_origin: row.countryOfOrigin ?? null,
    customs_tariff_number: row.customsTariffNumber ?? null,
    carnet_ata_description: row.carnetAtaDescription ?? null,
    carnet_ata_value: row.carnetAtaValue ?? null,
    carnet_ata_weight_unit: row.carnetAtaWeightUnit ?? null,
    tags:
      row.tagAssignments?.map((assignment) => assignment.tag).filter(Boolean) ??
      [],
    images:
      row.images?.map((image) => ({
        id: image.id,
        equipment_id: image.equipmentID,
        image_url: image.imageURL,
        display_order: image.displayOrder ?? null,
        created_at: toLegacyTimestamp(image.createdAt),
      })) ?? [],
  };
}

function toZeroEquipmentInput(equipment: EquipmentInsert | EquipmentUpdate) {
  return {
    ...(equipment.name !== undefined ? { name: equipment.name } : {}),
    ...(equipment.number !== undefined ? { number: equipment.number } : {}),
    ...(equipment.manufacturer !== undefined
      ? { manufacturer: equipment.manufacturer }
      : {}),
    ...(equipment.icon !== undefined ? { icon: equipment.icon } : {}),
    ...(equipment.new_price !== undefined
      ? { newPrice: equipment.new_price }
      : {}),
    ...(equipment.rental_price !== undefined
      ? { rentalPrice: equipment.rental_price }
      : {}),
    ...(equipment.is_serialized !== undefined
      ? { isSerialized: equipment.is_serialized }
      : {}),
    ...(equipment.stock_quantity !== undefined
      ? { stockQuantity: equipment.stock_quantity }
      : {}),
    ...(equipment.notes !== undefined ? { notes: equipment.notes } : {}),
    ...(equipment.parent_equipment_id !== undefined
      ? { parentEquipmentID: equipment.parent_equipment_id }
      : {}),
    ...(equipment.rentman_id !== undefined
      ? { rentmanID: equipment.rentman_id }
      : {}),
    ...(equipment.weight !== undefined ? { weight: equipment.weight } : {}),
    ...(equipment.width !== undefined ? { width: equipment.width } : {}),
    ...(equipment.height !== undefined ? { height: equipment.height } : {}),
    ...(equipment.length !== undefined ? { length: equipment.length } : {}),
    ...(equipment.volume !== undefined ? { volume: equipment.volume } : {}),
    ...(equipment.power !== undefined ? { power: equipment.power } : {}),
    ...(equipment.current_draw !== undefined
      ? { currentDraw: equipment.current_draw }
      : {}),
    ...(equipment.qr_code !== undefined ? { qrCode: equipment.qr_code } : {}),
    ...(equipment.external_remark !== undefined
      ? { externalRemark: equipment.external_remark }
      : {}),
    ...(equipment.location_in_warehouse !== undefined
      ? { locationInWarehouse: equipment.location_in_warehouse }
      : {}),
    ...(equipment.short_name !== undefined
      ? { shortName: equipment.short_name }
      : {}),
    ...(equipment.country_of_origin !== undefined
      ? { countryOfOrigin: equipment.country_of_origin }
      : {}),
    ...(equipment.customs_tariff_number !== undefined
      ? { customsTariffNumber: equipment.customs_tariff_number }
      : {}),
    ...(equipment.carnet_ata_description !== undefined
      ? { carnetAtaDescription: equipment.carnet_ata_description }
      : {}),
    ...(equipment.carnet_ata_value !== undefined
      ? { carnetAtaValue: equipment.carnet_ata_value }
      : {}),
    ...(equipment.carnet_ata_weight_unit !== undefined
      ? { carnetAtaWeightUnit: equipment.carnet_ata_weight_unit }
      : {}),
  };
}

async function reportZeroMutationFailure(
  serverResult: Promise<ZeroMutationResult>,
  fallback: string,
) {
  const result = await serverResult;
  if (result.type === "error") {
    throw new Error(result.error?.message || fallback);
  }
}

function useZeroMutation<TInput, TResult>({
  run,
  success,
  failure,
}: {
  run: (input: TInput) => Promise<TResult>;
  success: string;
  failure: string;
}) {
  const [isPending, setIsPending] = useState(false);
  const wrappedRun = async (input: TInput) => {
    setIsPending(true);
    try {
      return await run(input);
    } catch (error) {
      throw toFriendlyMutationError(error);
    } finally {
      setIsPending(false);
    }
  };
  return {
    mutate: (input: TInput, options?: MutationOptions<TResult>) => {
      void wrappedRun(input)
        .then((data) => {
          toast.success(success);
          options?.onSuccess?.(data);
        })
        .catch((error) => toast.error(`${failure}: ${error.message}`));
    },
    mutateAsync: wrappedRun,
    isPending,
  };
}

export const useAllEquipment = () => {
  const [data, details] = useZeroQuery(appQueries.equipmentCatalog.list());
  const equipment = ((data ?? []) as ZeroEquipmentRow[]).map(toLegacyEquipment);

  return {
    data: equipment,
    isLoading: details.type !== "complete" && equipment.length === 0,
    isFetching: details.type !== "complete",
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
};

export const useEquipment = (options: UseEquipmentOptions = {}) => {
  const { search, tagIds, isSerialized, page = 1 } = options;
  const { data: allEquipment, ...queryRest } = useAllEquipment();
  const { data: allTags } = useEquipmentTags();

  const hasTagFilter = tagIds && tagIds.length > 0;

  const expandedTagIds = useMemo(() => {
    if (!hasTagFilter || !allTags) return [];
    return getEquipmentTagDescendantIds(allTags, tagIds!);
  }, [hasTagFilter, allTags, tagIds]);

  const data = useMemo((): PaginatedEquipment => {
    let filtered = allEquipment ?? [];

    if (search) {
      const lower = search.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.name?.toLowerCase().includes(lower) ||
          item.manufacturer?.toLowerCase().includes(lower) ||
          item.number?.toLowerCase().includes(lower) ||
          item.short_name?.toLowerCase().includes(lower),
      );
    }

    if (isSerialized !== null && isSerialized !== undefined) {
      filtered = filtered.filter((item) => item.is_serialized === isSerialized);
    }

    if (hasTagFilter && expandedTagIds.length > 0) {
      filtered = filtered.filter((item) =>
        item.tags?.some((tag) => expandedTagIds.includes(tag.id)),
      );
    }

    const totalCount = filtered.length;
    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    const start = (page - 1) * PAGE_SIZE;
    const items = filtered.slice(start, start + PAGE_SIZE);

    return { items, totalCount, page, pageSize: PAGE_SIZE, totalPages };
  }, [allEquipment, search, isSerialized, hasTagFilter, expandedTagIds, page]);

  return { ...queryRest, data };
};

export const useSingleEquipment = (id: string | undefined) => {
  const query = id
    ? appQueries.equipmentCatalog.byID({ id })
    : appQueries.equipmentCatalog.byID({
        id: "00000000-0000-0000-0000-000000000000",
      });
  const [data, details] = useZeroQuery(query, { enabled: !!id });
  const equipment = data ? toLegacyEquipment(data as ZeroEquipmentRow) : null;

  return {
    data: equipment,
    isLoading: !!id && details.type !== "complete" && !equipment,
    isFetching: !!id && details.type !== "complete",
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
};

export const useCreateEquipment = () => {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<
    { equipment: EquipmentInsert; tagIds?: string[] },
    Equipment
  >({
    success: "Equipment created",
    failure: "Failed to create equipment",
    run: async ({ equipment, tagIds }) => {
      const id = generateUUID();
      const mutation = zero.mutate(
        appMutators.equipmentCatalog.create({
          id,
          ...toZeroEquipmentInput(equipment),
          tagIDs: tagIds ?? [],
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Equipment create rejected",
      );
      return {
        ...toLegacyEquipment({
          id,
          name: equipment.name,
          isSerialized: equipment.is_serialized,
          ...toZeroEquipmentInput(equipment),
        } as ZeroEquipmentRow),
        tags: [],
      };
    },
  });
};

export const useUpdateEquipment = () => {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<
    { id: string; equipment: EquipmentUpdate; tagIds?: string[] },
    { id: string }
  >({
    success: "Equipment updated",
    failure: "Failed to update equipment",
    run: async ({ id, equipment, tagIds }) => {
      const mutation = zero.mutate(
        appMutators.equipmentCatalog.update({
          id,
          ...toZeroEquipmentInput(equipment),
          ...(tagIds !== undefined ? { tagIDs: tagIds } : {}),
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Equipment update rejected",
      );
      return { id };
    },
  });
};

export const useSoftDeleteEquipment = () => {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<string, void>({
    success: "Equipment deleted",
    failure: "Failed to delete equipment",
    run: async (id) => {
      const mutation = zero.mutate(
        appMutators.equipmentCatalog.softDelete({ id }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Equipment delete rejected",
      );
    },
  });
};
